import { z } from 'zod'
import { fieldRegistry } from '../../../../forms/registry'
import { yesNoType } from '../bool-field'
import * as options from '../options'
import { preprocessString } from './helpers'

export const ElevatorSurveyBase = z.object({
  timestamp: z.string().datetime(),
  metadata: z.object({
    timeToComplete: z.number(), // seconds
    formVersion: preprocessString(),
  }),
})

const BasicInformation = z.object({
  // Core sections - always present
  // Site ID for new unit creation (optional, only used when creating new units)
  siteId: z.string().register(fieldRegistry, {
    label: 'Site',
    description: 'Select the site where this elevator is located',
    fieldType: 'searchable-select',
    placeholder: 'Choose site',
    options: 'sites',
  }),

  unitId: z.string().min(1, 'Unit ID is required').register(fieldRegistry, {
    label: 'Unit ID',
    placeholder: 'Enter unit identifier',
    description: 'Unique identifier for this elevator unit',
    fieldType: 'hidden',
  }),

  ahjId: z.string().min(1, 'AHJ ID is required').register(fieldRegistry, {
    label: 'AHJ ID',
    placeholder: 'AHJ identifier',
    description: 'AHJ identifier for this unit',
    fieldType: 'text',
  }),

  startDateMonth: z.enum(options.MonthOptions).register(fieldRegistry, {
    options: options.MonthOptions,
    label: 'MCP Start Month',
    description: 'The month the MCP starts',
    fieldType: 'select',
    placeholder: 'Select month',
  }),

  startDateYear: z.coerce
    .number()
    .max(new Date().getFullYear() + 1)
    .register(fieldRegistry, {
      options: Array.from(
        { length: 5 },
        (_, i) => `${new Date().getFullYear() + i}`,
      ),
      label: 'MCP Start Year',
      description: 'The year the MCP starts',
      fieldType: 'select',
      placeholder: new Date().getFullYear().toString(),
    }),

  surveyor: preprocessString().register(fieldRegistry, {
    label: 'Surveyor Name',
    placeholder: 'Enter surveyor name',
    description: 'Name of the individual performing the survey',
    fieldType: 'text',
  }),

  buildingDesignation: z.string().register(fieldRegistry, {
    label: 'Unit Name',
    placeholder: 'IE 1, North, 1-2, Service, etc',
    description: 'Local identifier used by building management',
    fieldType: 'text',
  }),

  manufacturer: z.string().register(fieldRegistry, {
    label: 'Manufacturer',
    placeholder: 'Enter manufacturer name',
    description: 'Elevator manufacturer (e.g., Otis, Schindler, KONE)',
    fieldType: 'typeahead',
    options: options.ManufacturersOptions,
  }),

  manufacturerSerial: z.string().register(fieldRegistry, {
    label: 'Controller Serial Number',
    placeholder: 'Enter controller serial number',
    description: 'Controller serial number for this unit',
    fieldType: 'text',
  }),
})

const NotesGeneral = z.object({
  execSoftwareRevision: preprocessString().optional().register(fieldRegistry, {
    label: 'Unique Software Identifier',
    description: 'Software revision of the elevator controller',
    fieldType: 'text',
    placeholder: 'Enter software version',
    tooltip:
      'This is the unique software version of the Executable Software shown in the User Interface. It must match with a sign on the controller door where the latest software version is manually recorded. The sign must be updated when new executable software is installed in the unit and the date it was changed. This applies to all controllers since the 2019 Edition.\n\nFor units without the sign, it is recommended the USI be recorded on all computer elevators.',
  }),

  generalNotes: preprocessString().optional().register(fieldRegistry, {
    label: 'Internal Notes',
    description:
      'Additional notes and observations about the elevator (internal use only)',
    fieldType: 'textarea',
    placeholder: 'Enter any additional notes or observations...',
  }),

  // Survey photos (up to 4)
  photos: z.array(z.string()).default([]).register(fieldRegistry, {
    label: 'Survey Photos',
    description: 'Upload photos documenting the elevator condition (up to 4)',
    fieldType: 'photos',
    maxPhotos: 4,
  }),

  // Billing indicator
  doNotRenew: yesNoType().optional().register(fieldRegistry, {
    label: 'Do Not Renew',
    description:
      'Indicator for billing purposes - should this unit not be renewed?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),
})

const MachineRoom = z.object({
  speed: z.coerce
    .number()
    .positive('Speed must be positive')
    .register(fieldRegistry, {
      label: 'Speed (FPM)',
      placeholder: '100',
      description: 'Rated speed of unit in feet per minute',
      fieldType: 'number',
    }),

  condition: z.enum(options.ConditionOptions).register(fieldRegistry, {
    label: 'Overall Condition',
    description: 'Overall condition assessment of the elevator',
    fieldType: 'select',
    options: options.ConditionOptions,
    tooltip: `The particular state of repair or ability to function of the elevator or any major sub-system in terms of the present machine room, hoistway and pit equipment with respect to its design or normal function. Does the elevator fully function as intended or are there some sub-systems disabled due to age, maladjustment or obsolescence.

  - **Mint** - Every sub-system is brand new, fully functioning and will likely remain so for at least one year.
  - **Very Good** - The system is less than 5 years old, well kept and all sub-systems function and will likely remain fully functioning for at least one year.
  - **Good** - The system is more than 5 years old but no components are obsolete and sub-systems all work as intended and can be repaired.
  - **Fair** - The system shows evidence that some sub-systems are having reliability issues with regard to functioning for a minimum of one year. Repairs are increasing as evidenced by call back records.
  - **Bad** - The system shows an increase in repairs and adjustments that reduce the systems up time. The sub-systems can still be repaired, however a lack of knowledge of replacement components is attributed to the problems.
  - **Very bad** - The system has multiple failures of some sub-components, replacement proposal have been offered by the maintenance company to address the failures.
  - **Worst** - The elevator is down at the time of the survey or is not performing to specifications due to obsolete components and/or extreme lack of maintenance.`,
  }),

  accumulatedWear: z
    .enum(options.AccumulatedWearOptions)
    .register(fieldRegistry, {
      label: 'Accumulated Wear',
      description: 'Assessment of accumulated wear on the elevator',
      fieldType: 'select',
      options: options.AccumulatedWearOptions,
      tooltip: `During use, many components of the elevator wear. As time goes on, wear can be influenced by design, maintenance procedures, incorrect lubrication, and environment. For every elevator, the accumulation of wear will influence ride quality, sub-system reliability, and up-time performance measured in terms of callbacks. The wear of components such as relays contacts, roller guide polyurethane, and motor controller components should be examined to evaluate the accumulated wear. Typically all sliding and rolling surfaces will show wear.

  - **No Wear** - Every sub-system or surface is brand new; fully functioning and will likely remain so for at least one year.
  - **Slight** - The system is less than 5 years old, well kept and all sub-systems function and will likely remain fully functioning for at least one year.
  - **Noticeable** - The system is more than 5 years old but no components are obsolete and sub-systems all work as intended and can be maintained with an acceptable ride quality.
  - **Very evident** - The system shows evidence that some sub-systems are having reliability issues with regard to functioning for a minimum of one year. Repairs and replacements of components are increasing as evidenced by callback records.
  - **Worn** - The system shows an increase in repairs, replacements,  and adjustments that reduce the systems up time. The sub-systems can still be repaired; however ride quality is noticeably different than the original design. Noises can be heard from equipment when standing in the car.
  - **Very Worn** - The system has multiple failures of some sub-components due to worn surfaces, electrical contacts and bearing surfaces. Replacement proposals have been offered by the maintenance company to address the failures.
  - **Replace** - The elevator is down at the time of the survey or is not performing to specifications due to obsolete components and/or extreme lack of maintenance.`,
    }),
  design: z.enum(options.DesignOptions).register(fieldRegistry, {
    label: 'Design Quality',
    description: 'Assessment of the elevator design quality',
    fieldType: 'select',
    options: options.DesignOptions,
    tooltip: `Over time, elevator equipment develops a history of characteristics that are tangible to the eye of a professional elevator person relative to equipment of another manufacturer. Such factors include OEM designs versus independent elevator company designs versus foreign designs, size and strength of materials used in manufacture, attention to detail of serviceability, ease of assembling/disassembling and appearance. Over time, designs have wear factors that require more maintenance tasks than designs that have more robust components requiring less maintenance tasks. Degradation of components and sub-system malfunctions occur earlier in less robust designs.

  - **Best** - The overall design is robust with components that show a design premise of strength, durability, and reliability, proven by the test if time and built to last with mechanical components that will likely remain so for the life cycle of the building.
  - **Great** - The overall design is robust with components which show a design premise of strength, durability, reliability, proved by the test if time and built to last and will likely remain so for the life cycle of 20 years.
  - **Good** - The overall design is less robust with components which show a design premise of average strength, average durability, not yet proven by the test if time and not built to last but will likely remain so for the life cycle of 15 years.
  - **Fair** - The overall design is less robust with components which show a design premise of minimal strength, minimal durability, not proven by the test if time, not built to last but will likely remain so for the life cycle of 10 years.
  - **Just OK** - The overall design is obviously less robust with components which show a design premise of bare minimal strength, minimal durability and appear that components will not last for a life cycle of 15 years.
  - **Poor** - The overall design is obviously less robust than most systems ever encountered, first impression is that the design cannot last any significant length of time with components which show a design premise of bare minimum strength, minimal durability and have had callbacks or retrofits on some of the failed components.
  - **Very Poor** - The overall design is less robust than any system ever seen; the history is that the design didn’t last more than 2 years before retrofits on some if the failed components were made. The system will require high maintenance for the life of the system.`,
  }),

  inherentQuality: z
    .enum(options.InherentQualityOptions)
    .register(fieldRegistry, {
      label: 'Inherent Quality',
      description: 'Assessment of the inherent quality of the elevator',
      fieldType: 'select',
      options: options.InherentQualityOptions,
      tooltip: `Elevator systems use many sub-components. There are several vendors of similar devices as well as OEM manufacturers. The general standard or grade of something [quality] can typically be related to the manufacturer of a device, the misapplication of a device, the reliability of device for a given amount of maintenance and the life of a device. The rating of the quality for a whole system is difficult unless the system is measured in terms of the components with the least quality - maybe everything else is high quality, but one sub-system has an inherent quality flaw, bringing more maintenance tasks necessary to make assure continued compliance.

  - **Highest** - The overall design is manufactured with components that have a long history of reliability, from name brand vendors (sales in excess of $20M per year) with more than 5 years of reliable operation to compare against.
  - **Great** - The overall design is manufactured with most components that have a long history of reliability, from name brand vendors (sales in excess of $20M per year) with more than 5 years of reliable operation to compare against. Off brand components are limited to non-safety sub-systems.
  - **Good** - The overall design is manufactured with a majority of components that have a long history of reliability, from name brand vendors (sales in excess of $20M per year) with more than 5 years of reliable operation to compare against. Off brand and novel components are not limited to non-safety sub-systems, but may be in safety systems requiring special maintenance requirements.
  - **Fair** - The overall design is manufactured with a majority of components from off brand vendors (sales less than $20M per year) with no history of reliable operation to compare against. Off brand and novel components are not limited to non-safety sub-systems, but may be in safety systems requiring special maintenance requirements.
  - **Just OK** - The overall design is manufactured with a majority of components from off brand vendors (sales less than $20M per year) with a history of unreliable operation.
  - **Poor** - The overall design is manufactured with a majority of components from unknown vendors with a history of unreliable operation.
  - **Lowest** - The overall design is manufactured with a majority of components with a history of unreliable operation as measured by callback history related to component quality. `,
    }),

  usage: z.enum(options.UsageOptions).register(fieldRegistry, {
    label: 'Usage Pattern',
    description: 'Primary usage pattern of the elevator',
    fieldType: 'select',
    options: options.UsageOptions,
    tooltip: `Elevator systems are in many different types of structures and buildings. Some elevators only run (open and close the doors, make an up or down run, open and close the doors) 20 times per day, others may run 1,300 times per day. The increased usage requires more frequent maintenance. This is simple number of runs per elevator per day. There is rarely any measurement of the number of runs per day that is evident to the surveyor, so in lieu of numbers of uses, we could use typical applications. Assuming a 1 minute run, the most any conveyance can run is 60 times an hour times 24 hrs - 1440 times a day.

  - **ADA** - The conveyance s only there to provide compliance with ADA requirements or the conveyance is of low use such as a small church, stage lift, or similar application. This would be less than 100 runs per day.
  - **Small Apartment/Condo** - Tenants exiting and entering their home, typically around commute times, use the conveyance. The conveyance does not have constant use all day. This would be less than 200 runs per day but more than 101 runs.
  - **Large Apartment/Condo** - In high-rise apartment/Condos, the number of rooms exceed 100, the elevators will be used more use to the higher number of residents. This would be less the 400 runs but more than 201 runs.
  - **Small Office** - In a low-rise office building, with less than 20 landings, the elevator is used primarily during business hours, 6 AM to 6 PM. The conveyances have peak systems to dispatch during peak periods. This would be less than 600 runs per day but more than 401 runs per day.
  - **Large Office** - In a high-rise office building, with more than 20 landings per bank, the elevator is used primarily during business hours, 6 AM to 6 PM but the increased number of workers demand more runs. The conveyances have peak systems to dispatch during peak periods. This would be less than 800 runs per day but more than 601 runs per day.
  - **Observation** - Where an elevator is used as a showpiece, such as an express to an observation deck or restaurant, the number of runs is not tied to typical peak service dispatching operation but more open to the public and is in constant motion. This would be less than 1,000 runs per day but more then 810 runs per day.
  - **Medical/Convalescent** - These conveyances are 24-hour use, based on demand at all times. Typical uses are hospitals, convalescent homes, and similar applications where there is a constant 24-hour population. This would be the highest level of demand, more than 1,000 runs per day.`,
  }),

  environment: z.enum(options.EnvironmentOptions).register(fieldRegistry, {
    label: 'Environmental Conditions',
    description: 'Assessment of the environmental conditions',
    fieldType: 'select',
    options: options.EnvironmentOptions,
    tooltip: `The environment of an elevator system encompasses where the machine room and hoistway are located in a building and the exposure to a non-conditioned atmosphere such as parking garages, factories, oil refineries, paper mills, or seaside exposures. This measure should include the worst area for the evaluation. Some elevators have an exposed machine room with the hoistway in a conditioned space or visa versa. So considering the worst condition, the machine room or the hoistway, the rating should be based on that.

  - **Very clean** - Both the machine room and hoistway are in spaces that the air is conditioned and filtered with little contamination from outside the building enters these spaces. Temperature and humidity are controlled.
  - **Mostly clean** - Both the machine room and hoistway are in spaces that the air is filtered with little contamination from outside the building. Temperature and humidity are not controlled, but the environment is very comfortable.
  - **Dusty** - Either the machine room and hoistway are in spaces that the air is unfiltered with light dusty contamination from outside the machinery space collect in the machinery spaces. Temperature and humidity are not controlled, but the environment is very comfortable.
  - **Grimy** - Either the machine room and hoistway are in spaces that the air is heavily ingrained with accumulated dirt or soot contamination from outside the machinery space that collect in the machinery spaces. Temperature and humidity may be controlled but the environment can be seen as visible debris after one month.
  - **Dirty** - Either the machine room and hoistway are in spaces that the air is heavily ingrained with accumulated dirt or soot contamination from inside or outside the machinery spaces that collect in the machinery spaces. Temperature and humidity may be controlled but the environment can be seen as visible debris after one week.
  - **Very dirty** - Either the machine room and hoistway are in spaces that the air is heavily ingrained with accumulated dirt or soot contamination from inside or outside the machinery space that collect in the machinery spaces. Temperature and humidity is not controlled but the environment can be seen as visible debris after one day.
  - **Filthy** - Either the machine room and hoistway are in spaces that are extremely or disgustingly dirty from contamination generated from outside the machinery space that collect in the machinery spaces. Temperature and humidity is controlled and the environment can be seen as visible debris after one day. `,
  }),

  technology: z.enum(options.TechnologyOptions).register(fieldRegistry, {
    label: 'Technology Level',
    description: 'Assessment of the technology level of the elevator',
    fieldType: 'select',
    options: options.TechnologyOptions,
    tooltip: `The age of the equipment is a direct reflection of some general designs that have changed over the years. Early technologies were open controllers on sheets of slate, to enclosed controllers with large relays, to enclosed controllers with smaller relays and then solid state (Transistor-Transistor, Diode-Transistor) controllers to finally computer controls in enclosures with filtered air. Each vintage of controller requires its own maintenance tasks, generally the older the system, the more maintenance required. The metrics reflect these revised designs to assist in evaluating and visually recognizing the technologies being surveyed.

  - **Computer** - The Operational Controller has a microprocessor board with busses on the PC Board for transmission of I/O, software instruction. Generally these are controllers designed after 1995. Typical examples - ThyssenKrupp TAC 50, Otis 411, GAL Galaxy, Smartrise, Elevator Controls.
  - **TTL/DTL** - The Operational Controller has PC Boards without a microprocessor, but individual boards that slide in and out of a card edge connector. Typical examples - Dover Solid State, Miprom, Otis Boolean.
  - **Ice Cube Relay** - The Operation Controller is a PC Board with only relay logic using enclosed plug-in relays and no TTL/DTL logic. Typical Examples - US Elevator.
  - **Open Relay** - The Operational Controller has open relays. Typical Examples - Westinghouse HY, DMR, CDM, using ES and EC relays; Otis 10/20 UCL, Otis AU, using 6164 Relays.
  - **Carbon Relays** - Older relays with carbon contacts. Typical Examples - Older GAL, KM White.
  - **Phenolic Board** - The Operational Controller with no enclosure, the base is of a phenolic board standing vertically on the floor with wire to relay connections drilled through the phenolic board from front to back using threaded studs and nuts. Typical Example - Spencer, Serge
  - **Slate Board** - The Operation Controller with no enclosure, the base controller is of a Slate Board standing vertically on the floor with wire to relay connections drilled through the slate board from front to back using threaded studs and nuts. Typical Examples - Otis SOB, AR, and very old control systems.`,
  }),

  typeOfUse: z.enum(options.TypeOfUseOptions).register(fieldRegistry, {
    label: 'Type of Use',
    description: 'Classification of elevator use type',
    fieldType: 'select',
    options: options.TypeOfUseOptions,
  }),

  driveType: z.enum(options.DriveTypeOptions).register(fieldRegistry, {
    label: 'Driving Machine Type',
    description: 'Type of drive system used',
    fieldType: 'select',
    options: options.DriveTypeOptions,
  }),

  // TODO: possibly call this tyoeOfMotorController
  motorController: z
    .enum(options.MotorControllerOptions)
    .register(fieldRegistry, {
      label: 'Motor Controller Type',
      description: 'Type of motor controller system',
      fieldType: 'select',
      options: options.MotorControllerOptions,
    }),

  // TODO: Should this be a typeahead field? It doesn't seem like it is in the original form
  drivingMachineManufacturerAndModel: preprocessString()
    .optional()
    .register(fieldRegistry, {
      label: 'Driving Machine Manufacturer - Model',
      description: 'Manufacturer and model of the driving machine',
      fieldType: 'text',
      placeholder: 'Enter manufacturer and model',
    }),

  controllerManufacturerAndModel: preprocessString()
    .optional()
    .register(fieldRegistry, {
      label: 'Controller Manufacturer - Model',
      description: 'Manufacturer and model of the controller',
      fieldType: 'typeahead',
      placeholder: 'Enter controller manufacturer and model',
      options: options.ControllerModelOptions,
    }),

  drivingMachineInstallYear: z.coerce
    .number()
    .min(1800)
    .register(fieldRegistry, {
      label: 'Driving Machine Install Year',
      description: 'Year the driving machine was installed',
      fieldType: 'number',
      placeholder: new Date().getFullYear().toString(),
    }),

  operationalControllerInstallYear: z.coerce
    .number()
    .min(1800)
    .register(fieldRegistry, {
      label: 'Operational Controller Install Year',
      description: 'Year the operational controller was installed',
      fieldType: 'number',
      placeholder: new Date().getFullYear().toString(),
    }),

  typeOfMotorController: z
    .enum(options.MotorControllerOptions)
    .register(fieldRegistry, {
      label: 'Motor Controller Type',
      description: 'Type of motor controller system',
      fieldType: 'select',
      options: options.MotorControllerOptions,
    }),

  hasEmergencyPower: yesNoType().register(fieldRegistry, {
    label: 'Has Emergency Power',
    description: 'Does the elevator have emergency power backup?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),

  hasMGSet: yesNoType().register(fieldRegistry, {
    label: 'Has Motor Generator Set',
    description: 'Does the elevator have a motor generator set?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),

  isMachineRoomLess: yesNoType().register(fieldRegistry, {
    label: 'Machine Roomless (MRL)',
    description: 'Is this a machine roomless elevator?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),

  carsShareMachineRoom: z.coerce
    .number()
    .min(1)
    .optional()
    .register(fieldRegistry, {
      label: 'How many elevators share this machine room?',
      description: 'Not used',
      placeholder: '1 if the car does not share a machine room',
      fieldType: 'number',
    }),

  // Hydraulic-specific fields
  hasFlexibleHose: yesNoType().register(fieldRegistry, {
    label: 'Has Flexible Hose',
    description: 'Does the hydraulic system use flexible hoses?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),

  hasPressureVessel: yesNoType().register(fieldRegistry, {
    label: 'Has Pressure Vessel',
    description: 'Does the hydraulic system have a pressure vessel?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),

  hasPressureSwitch: yesNoType().register(fieldRegistry, {
    label: 'Has Pressure Switch',
    description: 'Does the hydraulic system have pressure switches?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),

  hasBatteryLowering: yesNoType().register(fieldRegistry, {
    label: 'Has Battery Lowering',
    description: 'Does the hydraulic system have battery lowering capability?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),

  controlValveManufacturerModel: preprocessString().register(fieldRegistry, {
    label: 'Control Valve Manufacturer - Model',
    description: 'Manufacturer and model of hydraulic valves',
    fieldType: 'typeahead',
    placeholder: 'Enter control valve manufacturer and model',
    options: options.ControlValveModelOptions,
  }),

  // Traction-specific fields
  governorType: z.enum(options.GovernorTypeOptions).register(fieldRegistry, {
    label: 'Governor Type',
    description: 'Type of governor system',
    fieldType: 'select',
    options: options.GovernorTypeOptions,
  }),

  acopumdType: z
    .enum(options.ACOPUMDTypeOptions)
    .nullable()
    .optional()
    .register(fieldRegistry, {
      label: 'ACOP/UCMP Type',
      description: 'ACOP/UCMP classification if applicable',
      fieldType: 'select',
      options: options.ACOPUMDTypeOptions,
    }),

  brakeType: z.enum(options.BrakeTypeOptions).register(fieldRegistry, {
    label: 'Brake Type',
    description: 'Type of brake system',
    fieldType: 'select',
    options: options.BrakeTypeOptions,
  }),

  hasNonMetallicSheaveLiner: yesNoType()
    .nullable()
    .optional()
    .register(fieldRegistry, {
      label: 'Has non-metallic sheave liner AND steel rope?',
      description:
        'Does the elevator have non-metallic sheave liner AND steel rope?',
      fieldType: 'radio',
      options: options.YesNoOptions,
    }),

  hasCoatedRopes: yesNoType().nullable().optional().register(fieldRegistry, {
    label: 'Has Coated Ropes',
    description: 'Does the elevator use coated ropes?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),

  hasTractionLossDetection: yesNoType()
    .nullable()
    .optional()
    .register(fieldRegistry, {
      label: 'Has Traction Loss Detection',
      description: 'Does the elevator have traction loss detection?',
      fieldType: 'radio',
      options: options.YesNoOptions,
    }),

  hasBrokenSuspensionDetection: yesNoType()
    .nullable()
    .optional()
    .register(fieldRegistry, {
      label: 'Has Broken Suspension Detection',
      description: 'Does the elevator have broken suspension detection?',
      fieldType: 'radio',
      options: options.YesNoOptions,
    }),

  hasCounterWeights: yesNoType().nullable().optional().register(fieldRegistry, {
    label: 'Has Counterweights',
    description: 'Does the elevator have counterweights?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),
})

const CarTop = z.object({
  suspensionMembersInstallYear: z.coerce
    .number()
    .min(1800)
    .register(fieldRegistry, {
      label: 'Suspension Members Install Year',
      description: 'Year the suspension members (ropes/belts) were installed',
      fieldType: 'number',
      placeholder: new Date().getFullYear().toString(),
    }),

  interlocksInstallYear: z.coerce.number().min(1800).register(fieldRegistry, {
    label: 'Interlocks Install Year',
    description: 'Year the interlock system was installed',
    fieldType: 'number',
    placeholder: new Date().getFullYear().toString(),
  }),

  typeOfDoorOperator: z
    .enum(options.DoorOperatorOptions)
    .register(fieldRegistry, {
      label: 'Type of Door Operator',
      description: 'Type of door operator system',
      fieldType: 'select',
      options: options.DoorOperatorOptions,
    }),

  /**
   * AKA Has Broken Selector Switch
   */
  hasBrokenRopeSelectorSwitch: yesNoType().register(fieldRegistry, {
    label: 'Has Broken Selector Switch',
    description: 'Does the elevator have broken selector switch detection?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),

  doorRestrictorType: z
    .enum(options.CarDoorRestrictorTypeOptions)
    .register(fieldRegistry, {
      label: 'Type of Car Door Restrictor',
      description: 'Type of car door restrictor devices',
      fieldType: 'select',
      options: options.CarDoorRestrictorTypeOptions,
    }),

  hasCarSafeties: yesNoType().optional().register(fieldRegistry, {
    label: 'Has Car Safeties',
    description: 'Does the elevator have car safety devices?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),

  suspensionMeansType: z
    .enum(options.SuspensionMeansOptions)
    .register(fieldRegistry, {
      label: 'Suspension Means',
      description: 'Type of suspension system used',
      fieldType: 'select',
      options: options.SuspensionMeansOptions,
    }),

  hasSeismicDevices: yesNoType().register(fieldRegistry, {
    label: 'Has Seismic Protection',
    description: 'Does the elevator have seismic protection devices?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),

  hasCounterweightSafeties: yesNoType().register(fieldRegistry, {
    label: 'Has Counterweight Safeties',
    description: 'Does the elevator have counterweight safety devices?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),

  guideType: z.enum(options.GuideTypeOptions).register(fieldRegistry, {
    label: 'Type of Guides',
    description: 'Type of guide rail system',
    fieldType: 'select',
    options: options.GuideTypeOptions,
  }),

  doorOperatorManufacturerAndModel: preprocessString().register(fieldRegistry, {
    label: 'Door Operator Manufacturer - Model',
    description: 'Manufacturer and model of the door operator',
    fieldType: 'typeahead',
    placeholder: 'Enter door operator manufacturer and model',
    options: options.DoorOperatorModelOptions,
  }),
})

const InCar = z.object({
  hoistwayDoorType: z
    .enum(options.HoistwayDoorTypeOptions)
    .register(fieldRegistry, {
      label: 'Hoistway Door Type',
      description: 'Type of hoistway door configuration',
      fieldType: 'select',
      options: options.HoistwayDoorTypeOptions,
    }),

  carDoorType: z.enum(options.CarDoorTypeOptions).register(fieldRegistry, {
    label: 'Car Door Type',
    description: 'Type of car door configuration',
    fieldType: 'select',
    options: options.CarDoorTypeOptions,
  }),

  // Building and accessibility information
  /**
   * AKA Number of Hoistway Entrances
   */
  numLandings: z.coerce.number().min(1).register(fieldRegistry, {
    label: 'Number of Hoistway Entrances',
    description: 'Total number of landings served by this elevator',
    fieldType: 'number',
    placeholder: '2',
  }),

  // TODO: Update to use NumberOfCarOpeningsOptions
  /**
   * AKA Number of Car Openings
   */
  numberOfOpenings: z.coerce.number().min(1).register(fieldRegistry, {
    label: 'Number of Car Openings',
    description: 'Number of openings in the elevator car',
    fieldType: 'number',
    placeholder: '1',
  }),

  doorOpeningMethod: z
    .enum(options.DoorOpeningMethodOptions)
    .register(fieldRegistry, {
      label: 'Door Opening Method',
      description: 'Method used to operate car doors',
      fieldType: 'select',
      options: options.DoorOpeningMethodOptions,
    }),

  doorPanelsType: z
    .enum(options.DoorPanelsTypeOptions)
    .register(fieldRegistry, {
      label: 'Has Multi-Door Panels',
      description: 'Does the car have multiple door panels?',
      fieldType: 'select',
      options: options.DoorPanelsTypeOptions,
    }),

  hasHoistwayAccess: yesNoType().register(fieldRegistry, {
    label: 'Has Hoistway Access',
    description: 'Is there hoistway access from inside the car?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),

  hasFirefighterService: yesNoType().register(fieldRegistry, {
    label: "Has Firefighters' Service",
    description: 'Does the elevator supports firefighter service?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),

  isObservationElevator: yesNoType().register(fieldRegistry, {
    label: 'Is Observation Elevator',
    description: 'Is this an observation elevator with glass panels?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),

  hasVisionPanels: yesNoType().register(fieldRegistry, {
    label: 'Has Vision Panels',
    description: 'Do the car doors have vision panels?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),
})

const Pit = z.object({
  bufferType: z.enum(options.BufferTypeOptions).register(fieldRegistry, {
    label: 'Buffer Type',
    description: 'Type of buffer system used in the pit',
    fieldType: 'select',
    options: options.BufferTypeOptions,
  }),

  hasBufferSwitches: yesNoType().register(fieldRegistry, {
    label: 'Has Buffer Switches',
    description: 'Does the elevator have buffer switches in the pit?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),

  hasOverspeedValve: yesNoType().register(fieldRegistry, {
    label: 'Has Overspeed Valve',
    description: 'Does the elevator have an overspeed valve?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),

  hasPlungerGripper: yesNoType().optional().register(fieldRegistry, {
    label: 'Has Plunger Gripper',
    description: 'Does the elevator have a plunger gripper?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),

  hasCylinderCorrosionProtection: yesNoType()
    .optional()
    .register(fieldRegistry, {
      label: 'Has Cylinder Corrosion Protection Monitoring',
      description:
        'Does the elevator have cylinder corrosion protection monitoring?',
      fieldType: 'radio',
      options: options.YesNoOptions,
    }),

  numCarsSharePit: z.coerce.number().min(1).optional().register(fieldRegistry, {
    label: 'How many cars share a pit with this elevator?',
    placeholder: '1 if the car does not share a pit',
    description: 'Not used',
    fieldType: 'number',
  }),

  hasCompensation: yesNoType().register(fieldRegistry, {
    label: 'Has Compensation',
    description: 'Does the elevator have a compensation system?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),

  hasCompensationSheave: yesNoType().register(fieldRegistry, {
    label: 'Has Compensation Sheave',
    description: 'Does the elevator have compensation sheaves in the pit?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),
})

const RackPinion = z.object({
  typeOfMast: z.enum(options.MastTypeOptions).register(fieldRegistry, {
    label: 'Type of Mast',
    description: 'Configuration of the mast system',
    fieldType: 'select',
    options: options.MastTypeOptions,
  }),

  numberOfMastSections: z.coerce.number().min(1).register(fieldRegistry, {
    label: 'Number of Mast Sections',
    description: 'Total number of mast sections',
    fieldType: 'number',
    placeholder: '1',
  }),

  numberOfMastTies: z.coerce.number().min(0).register(fieldRegistry, {
    label: 'Number of Mast Ties',
    description: 'Total number of mast tie connections',
    fieldType: 'number',
    placeholder: '0',
  }),

  numberOfMotors: z.coerce.number().min(1).register(fieldRegistry, {
    label: 'Number of Motors',
    description: 'Total number of drive motors',
    fieldType: 'number',
    placeholder: '1',
  }),

  travelingCable: z
    .enum(options.TravelingCableTypeOptions)
    .register(fieldRegistry, {
      label: 'Traveling Cable Type',
      description: 'Type of traveling cable system used',
      fieldType: 'select',
      options: options.TravelingCableTypeOptions,
    }),

  hasUnderPlatformObstructionDevice: yesNoType().register(fieldRegistry, {
    label: 'Has Under-Platform Obstruction Device',
    description: 'Does the elevator have under-platform obstruction detection?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),

  pitPermitRequired: yesNoType().clone().register(fieldRegistry, {
    label: 'Pit Permit Required',
    description: 'Is a pit permit required for this installation?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),

  yearGearboxOilChanged: z.coerce
    .number()
    .min(1900)
    .max(new Date().getFullYear())
    .optional()
    .nullable()
    .register(fieldRegistry, {
      label: 'Year Gearbox Oil Changed',
      description: 'Year when gearbox oil was last changed',
      fieldType: 'number',
      placeholder: new Date().getFullYear().toString(),
    }),
})

const EscalatorMovingWalkInclinedElevatorFields = z.object({
  hasHandrailSpeedMonitoring: yesNoType().optional().register(fieldRegistry, {
    label: 'Has Handrail Speed Sensors',
    description: 'Does the escalator have handrail speed sensors?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),

  hasLevelPalletDevice: yesNoType().optional().register(fieldRegistry, {
    label: 'Has Level Pallet Device',
    description: 'Does the escalator have level pallet device?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),

  hasCombImpactDetection: yesNoType().optional().register(fieldRegistry, {
    label: 'Has Comb Impact Detection',
    description: 'Does the escalator have comb impact detection?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),

  hasMissingPalletDevice: yesNoType().optional().register(fieldRegistry, {
    label: 'Has Missing Pallet Detection',
    description: 'Does the escalator have missing pallet detection?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),

  hasAntiSlideDevices: yesNoType().optional().register(fieldRegistry, {
    label: 'Has Anti-slide Devices',
    description: 'Does the escalator have anti-slide devices?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),

  hasCeilingGuards: yesNoType().optional().register(fieldRegistry, {
    label: 'Has Ceiling Guards',
    description: 'Does the escalator have ceiling guards?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),

  /**
   * AKA Has Broken Drive Chain Device
   */
  hasDriveChainDeviceSwitch: yesNoType().optional().register(fieldRegistry, {
    label: 'Has Drive Chain Device Switch',
    description: 'Does the escalator have drive chain device switches?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),

  hasGateSwitch: yesNoType().optional().register(fieldRegistry, {
    label: 'Has Gate Switch',
    description: 'Does the inclined elevator have gate switches?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),

  hasHoistwayDoorSwitch: yesNoType().optional().register(fieldRegistry, {
    label: 'Has Hoistway Door Switch',
    description: 'Does the inclined elevator have hoistway door switches?',
    fieldType: 'radio',
    options: options.YesNoOptions,
  }),
})

export const AllElevatorSurveyFields = z.object({
  ...BasicInformation.shape,
  ...MachineRoom.shape,
  ...CarTop.shape,
  ...InCar.shape,
  ...Pit.shape,
  ...NotesGeneral.shape,
  // Specialized fields
  ...EscalatorMovingWalkInclinedElevatorFields.shape,
  ...RackPinion.shape,
})

export type AllElevatorSurveyFields = z.infer<typeof AllElevatorSurveyFields>
